import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { AddressVerificationDialog } from '@/components/forms/Address/AddressVerificationDialog'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { normalizeAddressRegionCode } from '@/lib/address-region-options'
import { Country } from '@/lib/enums/Country'
import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import { useAppForm } from '@/lib/form'
import dayjs from '@/lib/dayjs'
import { toast } from 'sonner'
import { getErrorMessage, normalizePhoneDigits, toYmdFromDate } from '@/lib/utils'
import { deliveryFormSchema } from '@/lib/services/deliveries/deliveries.schema'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { calculateCheckoutDeliveryFee } from '@/lib/services/checkout/checkout.api'
import {
  getPosFulfillmentDateBlockKind,
  getPosFulfillmentDateBlockTooltip,
  getPosFulfillmentDateWarning,
  getPosFulfillmentDateAvailability,
} from '@/lib/pos/fulfillment-availability'
import { usePosFulfillmentAvailabilityNow } from './usePosFulfillmentAvailabilityNow'
import {
  buildLimitedOrderDeliveryAddressFields,
  buildOrderDeliveryAddressFieldsFromPlace,
  limitOrderDeliveryAddressFields,
} from '@/lib/order-address-limits'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import {
  isSuggestedAddressUsable,
  resolveDeliveryDestinationName,
  resolveProjectedDeliveryDestinationName,
  suggestedAddressMatchesTypedCore,
} from '@/components/forms/Orders/fulfillment-destination.helpers'
import { isValidCoordinates } from '@/components/forms/Locations/DeliveryAreaMap/utils'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { calculateCartTaxAmount } from '@/lib/services/carts/carts.api'
import { getOrderQuoteRelayFeeControls } from '@/lib/order-quote'
import { useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { RecipientSearch } from './RecipientSearch'
import {
  PosDeliveryAddressFields,
  PosFulfillmentDateField,
  PosSpecialInstructionsField,
} from './DeliveryFulfillmentFields'
import {
  getPosSubscriptionScheduleDefaults,
  PosSubscriptionScheduleField,
} from './PosSubscriptionScheduleField'

const UnableToVerifyDeliveryArea = 'unable_to_verify_delivery_area'
const OutsideAreaRecipientPhoneRequiredMessage =
  'Add a 10-digit recipient phone number for delivery outside your normal area.'

function canUseSavedOutsideAreaDelivery(cart) {
  const relayFeeControls = getOrderQuoteRelayFeeControls(cart)

  return (
    relayFeeControls.available &&
    relayFeeControls.deliveryServiceabilityStatus ===
      DeliveryServiceabilityStatus.OutsideDeliveryArea.Value
  )
}

export function getDeliveryFormDefaults(cart) {
  return {
    deliveryAddress: {
      name: resolveProjectedDeliveryDestinationName(cart),
      line1: cart?.delivery_address?.line1 || '',
      line2: cart?.delivery_address?.line2 || '',
      city: cart?.delivery_address?.city || '',
      state: normalizeAddressRegionCode(cart?.delivery_address?.state),
      postal_code: cart?.delivery_address?.postal_code || '',
      postal_code_suffix: cart?.delivery_address?.postal_code_suffix ?? null,
      google_place_id: cart?.delivery_address?.google_place_id || null,
      is_verified: cart?.delivery_address?.is_verified ?? false,
    },
    deliveryDate: cart?.fulfillment_due_date
      ? dayjs.utc(cart.fulfillment_due_date).format('YYYY-MM-DD')
      : '',
    preferredWeekday: getPosSubscriptionScheduleDefaults(cart).preferredWeekday,
    recipientName: cart?.recipient_name || '',
    recipientPhone: cart?.recipient_phone || '',
    recipientEmail: cart?.recipient_email || '',
    specialInstructions: cart?.special_instructions || '',
  }
}

function resolveIsRooftop(value) {
  if (typeof value?.isRooftop === 'boolean') {
    return value.isRooftop
  }

  if (value?.locationType) {
    return value.locationType === 'ROOFTOP' && value.partialMatch !== true
  }

  return null
}

function deliveryAddressSignature(address = {}) {
  return [
    address.line1,
    address.line2,
    address.city,
    normalizeAddressRegionCode(address.state),
    address.postal_code,
  ]
    .map((value) => String(value ?? '').trim())
    .join('|')
}

export function getOutsideDeliveryAreaFieldErrors({
  deliveryAddress,
  canUseFsnNetwork,
  isOutsideDeliveryArea,
}) {
  if (canUseFsnNetwork || !isOutsideDeliveryArea) {
    return null
  }

  return {
    'deliveryAddress.postal_code': [
      deliveryAddress?.lat != null
        ? 'Address is outside of the delivery area'
        : 'Zip code outside of delivery range',
    ],
  }
}

export function getOutsideAreaRecipientPhoneFieldErrors({
  canUseFsnNetwork,
  isOutsideDeliveryArea,
  recipientPhone,
}) {
  if (
    !canUseFsnNetwork ||
    !isOutsideDeliveryArea ||
    normalizePhoneDigits(recipientPhone).length === 10
  ) {
    return null
  }

  return {
    recipientPhone: [OutsideAreaRecipientPhoneRequiredMessage],
  }
}

function clearFieldErrors(form, fieldName) {
  form.setFieldMeta(fieldName, (meta) => {
    const errors = Array.isArray(meta?.errors) ? meta.errors : []
    const errorMap = meta?.errorMap ?? {}

    if (errors.length === 0 && Object.keys(errorMap).length === 0) {
      return meta
    }

    return {
      ...meta,
      errors: [],
      errorMap: {},
    }
  })
}

function buildDeliveryFeeAddressPayload(deliveryAddress) {
  return {
    line1: deliveryAddress.line1 ?? null,
    line2: deliveryAddress.line2 ?? null,
    city: deliveryAddress.city ?? null,
    state: deliveryAddress.state ?? null,
    postal_code: deliveryAddress.postal_code ?? null,
    postal_code_suffix: deliveryAddress.postal_code_suffix ?? null,
    country: deliveryAddress.country ?? Country.UnitedStates.Value,
    lat: deliveryAddress.lat ?? null,
    lng: deliveryAddress.lng ?? null,
    google_place_id: deliveryAddress.google_place_id ?? null,
    is_rooftop: deliveryAddress.is_rooftop ?? null,
    is_verified: Boolean(deliveryAddress.is_verified),
  }
}

async function canDeliverToAddress({
  orgId,
  cartId: initialCartId,
  ensureCartId,
  location,
  deliveryAddress,
}) {
  let cartId = initialCartId ?? null

  if (!cartId) {
    try {
      cartId = (await ensureCartId?.()) ?? null
    } catch (error) {
      toast.error('Unable to verify delivery area', {
        description: getErrorMessage(error),
      })
      return {
        cartId: null,
        canUseFsnNetwork: false,
        ok: false,
        serviceabilityStatus: UnableToVerifyDeliveryArea,
      }
    }
  }

  if (!cartId || !location?.id) {
    toast.error('Unable to verify delivery area', {
      description: 'Please try again before saving this delivery address.',
    })
    return {
      cartId,
      canUseFsnNetwork: false,
      ok: false,
      serviceabilityStatus: UnableToVerifyDeliveryArea,
    }
  }

  try {
    const response = await calculateCheckoutDeliveryFee({
      organizationId: orgId,
      cartId,
      data: {
        fulfillment_type: 'delivery',
        location_id: location.id,
        address: buildDeliveryFeeAddressPayload(deliveryAddress),
      },
    })
    const serviceabilityStatus =
      response?.serviceability_status ?? response?.data?.serviceability_status
    const canUseFsnNetwork = Boolean(
      response?.pos_outside_area_delivery_available ??
      response?.data?.pos_outside_area_delivery_available
    )
    const deliveryPolicy = response?.data ?? response

    return {
      cartId,
      canUseFsnNetwork,
      deliveryPolicy,
      ok: serviceabilityStatus === DeliveryServiceabilityStatus.Deliverable.Value,
      serviceabilityStatus,
    }
  } catch (error) {
    toast.error('Unable to verify delivery area', {
      description: getErrorMessage(error),
    })
    return {
      cartId,
      canUseFsnNetwork: false,
      ok: false,
      serviceabilityStatus: UnableToVerifyDeliveryArea,
    }
  }
}

function resolvedLocalDeliveryPolicy(deliveryCheck) {
  return deliveryCheck?.ok && Array.isArray(deliveryCheck.deliveryPolicy?.fulfillment_windows)
    ? deliveryCheck.deliveryPolicy
    : null
}

function isDeliveryDatePastCutoff(deliveryPolicy, deliveryDate) {
  return Boolean(
    deliveryPolicy?.effective_delivery_cutoff &&
    deliveryPolicy?.earliest_delivery_date &&
    deliveryDate &&
    deliveryDate < deliveryPolicy.earliest_delivery_date
  )
}

export function DeliveryModal({
  open,
  onOpenChange,
  location,
  onConfirm,
  cart,
  cartId = cart?.id ?? null,
  createCartWithDefaults,
}) {
  const { orgId } = useParams({ strict: false })
  const { geocodeAddress, geocodePlaceId } = usePlacesAutocomplete()
  const availabilityNow = usePosFulfillmentAvailabilityNow(open)
  const [deliveryPolicy, setDeliveryPolicy] = useState(null)
  const effectiveAvailabilityLocation = useMemo(
    () =>
      deliveryPolicy
        ? {
            ...location,
            earliest_delivery_date: deliveryPolicy.earliest_delivery_date,
            fulfillment_windows: deliveryPolicy.fulfillment_windows,
          }
        : location,
    [deliveryPolicy, location]
  )
  const deliveryDateAvailability = useMemo(() => {
    if (
      !open ||
      !(
        effectiveAvailabilityLocation?.is_delivery ||
        effectiveAvailabilityLocation?.fulfillment_windows
      )
    ) {
      return null
    }

    return getPosFulfillmentDateAvailability(effectiveAvailabilityLocation, {
      isPickup: false,
      now: availabilityNow,
    })
  }, [availabilityNow, effectiveAvailabilityLocation, open])

  const initialPlaceMeta = {
    lat: cart?.delivery_address?.lat ?? null,
    lng: cart?.delivery_address?.lng ?? null,
    placeId: cart?.delivery_address?.google_place_id || null,
    isRooftop: cart?.delivery_address?.is_rooftop ?? null,
  }
  const placeMetaRef = useRef(initialPlaceMeta)
  const deliveryPolicyRequestIdRef = useRef(0)
  const automaticPolicyAddressSignatureRef = useRef(null)
  const acceptedTypedAddressRef = useRef(null)
  const pendingVerifiedAddressRef = useRef(null)
  const [verifyPrompt, setVerifyPrompt] = useState(null)
  const [isOutsideAreaDelivery, setIsOutsideAreaDelivery] = useState(
    canUseSavedOutsideAreaDelivery(cart)
  )
  const requiresOutsideAreaRecipientPhone = isOutsideAreaDelivery
  const updatePlaceMeta = useCallback((nextPlaceMeta) => {
    placeMetaRef.current = nextPlaceMeta
  }, [])
  const clearDeliveryPolicy = useCallback(() => {
    deliveryPolicyRequestIdRef.current += 1
    setDeliveryPolicy(null)
  }, [])

  const form = useAppForm({
    defaultValues: getDeliveryFormDefaults(cart),
    validators: {
      onSubmit: deliveryFormSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        const deliveryPolicyRequestId = deliveryPolicyRequestIdRef.current + 1
        deliveryPolicyRequestIdRef.current = deliveryPolicyRequestId
        const isCurrentDeliveryPolicyRequest = () =>
          deliveryPolicyRequestIdRef.current === deliveryPolicyRequestId
        const pendingVerifiedAddress = pendingVerifiedAddressRef.current
        pendingVerifiedAddressRef.current = null
        const addr = pendingVerifiedAddress || value?.deliveryAddress || {}
        let validationCartId = cartId
        const ensureCartId = async () => {
          if (validationCartId) {
            return validationCartId
          }

          const newCart = await createCartWithDefaults?.()
          validationCartId = newCart?.id ?? null

          return validationCartId
        }
        let deliveryAddress = {
          ...addr,
          country: Country.UnitedStates.Value,
        }

        // Validate state matches zip code via Google Geocoding
        const stateTrimmed = (addr.state ?? '').trim().toUpperCase()
        const postalTrimmed = (addr.postal_code ?? '').trim()
        if (stateTrimmed && postalTrimmed.length === 5) {
          const zipQuery = `${postalTrimmed}, ${Country.UnitedStates.Value}`
          const geocoded = await geocodeAddress(zipQuery)
          if (!isCurrentDeliveryPolicyRequest()) return

          if (geocoded?.administrative_area_level_1) {
            const canonicalState = normalizeAddressRegionCode(geocoded.administrative_area_level_1)
            if (stateTrimmed !== canonicalState) {
              return {
                fields: {
                  'deliveryAddress.state': [
                    `State does not match zip code. For ${postalTrimmed} the state should be ${geocoded.administrative_area_level_1}.`,
                  ],
                },
              }
            }
          }
        }

        const currentPlaceMeta = placeMetaRef.current
        const hasCurrentCoords = isValidCoordinates(currentPlaceMeta.lat, currentPlaceMeta.lng)
        const typedAddressSignature = deliveryAddressSignature(addr)
        const acceptedTypedAddress = acceptedTypedAddressRef.current === typedAddressSignature

        if (!hasCurrentCoords && !acceptedTypedAddress) {
          const typedString = [
            addr.line1,
            addr.line2,
            `${addr.city || ''}${addr.state ? `, ${addr.state}` : ''} ${addr.postal_code || ''}`.trim(),
            Country.UnitedStates.Value,
          ]
            .filter(Boolean)
            .join(', ')
            .replace(/\s+/g, ' ')
            .trim()

          const suggested = await geocodeAddress(typedString)
          if (!isCurrentDeliveryPolicyRequest()) return

          if (isSuggestedAddressUsable(suggested)) {
            if (suggestedAddressMatchesTypedCore(addr, suggested)) {
              const limitedAddressFields = buildLimitedOrderDeliveryAddressFields(suggested, addr)
              const placeId = suggested.placeId ?? null
              const nextPlaceMeta = {
                lat: suggested.lat ?? null,
                lng: suggested.lng ?? null,
                placeId,
                isRooftop: resolveIsRooftop(suggested),
              }

              const exactMatchDeliveryAddress = {
                ...addr,
                ...limitedAddressFields,
                country: Country.UnitedStates.Value,
                google_place_id: placeId,
                lat: nextPlaceMeta.lat,
                lng: nextPlaceMeta.lng,
                is_verified: Boolean(placeId),
                is_rooftop: Boolean(placeId ? nextPlaceMeta.isRooftop : false),
              }
              deliveryAddress = exactMatchDeliveryAddress
              setDeliveryAddressFields(deliveryAddress)
              updatePlaceMeta(nextPlaceMeta)
            } else {
              setVerifyPrompt({
                typedAddress: addr,
                typedString,
                suggested,
              })

              return
            }
          } else {
            deliveryAddress = {
              ...addr,
              country: Country.UnitedStates.Value,
              google_place_id: null,
              is_verified: false,
              is_rooftop: false,
            }
          }
        } else {
          let isRooftop = currentPlaceMeta.isRooftop

          if (currentPlaceMeta.placeId && isRooftop === null) {
            const geocodedPlace = await geocodePlaceId(currentPlaceMeta.placeId)
            if (!isCurrentDeliveryPolicyRequest()) return

            isRooftop = resolveIsRooftop(geocodedPlace)
          }

          deliveryAddress = {
            ...deliveryAddress,
            lat: currentPlaceMeta.lat,
            lng: currentPlaceMeta.lng,
            google_place_id: currentPlaceMeta.placeId ?? null,
            is_verified: Boolean(currentPlaceMeta.placeId),
            is_rooftop: Boolean(currentPlaceMeta.placeId ? isRooftop : false),
          }
        }

        // Delivery area check
        if (deliveryAddress.postal_code) {
          const deliveryCheck = await canDeliverToAddress({
            orgId,
            cartId: validationCartId,
            ensureCartId,
            location,
            deliveryAddress,
          })
          if (!isCurrentDeliveryPolicyRequest()) return

          validationCartId = deliveryCheck.cartId ?? validationCartId
          const resolvedDeliveryPolicy = resolvedLocalDeliveryPolicy(deliveryCheck)
          const cutoffWarningWasVisible = isDeliveryDatePastCutoff(
            deliveryPolicy,
            value.deliveryDate
          )
          setDeliveryPolicy(resolvedDeliveryPolicy)
          if (!deliveryCheck.ok) {
            const isOutsideDeliveryArea =
              deliveryCheck.serviceabilityStatus ===
              DeliveryServiceabilityStatus.OutsideDeliveryArea.Value
            setIsOutsideAreaDelivery(deliveryCheck.canUseFsnNetwork && isOutsideDeliveryArea)

            const fieldErrors = getOutsideDeliveryAreaFieldErrors({
              deliveryAddress,
              canUseFsnNetwork: deliveryCheck.canUseFsnNetwork,
              isOutsideDeliveryArea,
            })

            if (fieldErrors) {
              return {
                fields: fieldErrors,
              }
            }

            if (deliveryCheck.canUseFsnNetwork && isOutsideDeliveryArea) {
              const phoneFieldErrors = getOutsideAreaRecipientPhoneFieldErrors({
                canUseFsnNetwork: deliveryCheck.canUseFsnNetwork,
                isOutsideDeliveryArea,
                recipientPhone: value.recipientPhone,
              })

              if (phoneFieldErrors) {
                return {
                  fields: phoneFieldErrors,
                }
              }
            } else {
              return {
                fields: {
                  'deliveryAddress.postal_code': [
                    deliveryCheck.serviceabilityStatus === UnableToVerifyDeliveryArea
                      ? 'We could not verify this delivery address. Please try again before saving.'
                      : deliveryCheck.serviceabilityStatus ===
                          DeliveryServiceabilityStatus.AddressNotVerified.Value
                        ? 'Choose a verified delivery address inside the delivery area.'
                        : deliveryAddress.lat != null
                          ? 'Address is outside of the delivery area'
                          : 'Zip code outside of delivery range',
                  ],
                },
              }
            }
          } else {
            setIsOutsideAreaDelivery(false)

            if (
              !cutoffWarningWasVisible &&
              isDeliveryDatePastCutoff(resolvedDeliveryPolicy, value.deliveryDate)
            ) {
              return
            }
          }
        }

        if (onConfirm) {
          if (validationCartId && location?.id) {
            try {
              await calculateCartTaxAmount({
                organizationId: orgId,
                cartId: validationCartId,
                data: {
                  location_id: location.id,
                  fulfillment_type: 'delivery',
                  address: {
                    line1: deliveryAddress.line1,
                    line2: deliveryAddress.line2,
                    city: deliveryAddress.city,
                    state: deliveryAddress.state,
                    postal_code: deliveryAddress.postal_code,
                    postal_code_suffix: deliveryAddress.postal_code_suffix,
                    country: deliveryAddress.country ?? Country.UnitedStates.Value,
                    lat: deliveryAddress.lat ?? null,
                    lng: deliveryAddress.lng ?? null,
                  },
                },
              })
              if (!isCurrentDeliveryPolicyRequest()) return
            } catch (error) {
              if (!isCurrentDeliveryPolicyRequest()) return

              toast.error('Unable to calculate tax for this address', {
                description: getErrorMessage(error),
              })
              return
            }
          }
          try {
            await onConfirm({
              cartId: validationCartId,
              date: value.deliveryDate,
              ...(value.preferredWeekday ? { preferredWeekday: value.preferredWeekday } : {}),
              deliveryAddress,
              recipientName: value.recipientName,
              recipientPhone: value.recipientPhone,
              recipientEmail: value.recipientEmail,
              specialInstructions: value.specialInstructions,
            })
            formApi.reset()
            updatePlaceMeta({
              lat: null,
              lng: null,
              placeId: null,
              isRooftop: null,
            })
            onOpenChange(false)
          } catch (error) {
            if (error?.response?.data?.errors) {
              return { fields: error.response.data.errors }
            }
            toast.error('Failed to save delivery information', {
              description: getErrorMessage(error),
            })
          }
        } else {
          formApi.reset()
          onOpenChange(false)
        }
      },
    },
  })

  const refreshDeliveryPolicy = useCallback(
    async (deliveryAddress) => {
      if (!location?.is_delivery_cutoff_overrides_enabled) {
        setDeliveryPolicy(null)
        return null
      }

      const requestId = deliveryPolicyRequestIdRef.current + 1
      deliveryPolicyRequestIdRef.current = requestId

      if (!deliveryAddress?.postal_code) {
        return null
      }

      if (!cartId) {
        return null
      }

      const deliveryCheck = await canDeliverToAddress({
        orgId,
        cartId,
        location,
        deliveryAddress,
      })

      if (deliveryPolicyRequestIdRef.current !== requestId) {
        return null
      }

      setDeliveryPolicy(resolvedLocalDeliveryPolicy(deliveryCheck))

      return deliveryCheck
    },
    [cartId, location, orgId]
  )
  const refreshSavedAddressDeliveryPolicy = useEffectEvent((deliveryAddress) => {
    void refreshDeliveryPolicy(deliveryAddress)
  })

  useEffect(() => {
    if (!open || !cart?.delivery_address?.postal_code || canUseSavedOutsideAreaDelivery(cart)) {
      automaticPolicyAddressSignatureRef.current = null
      return
    }

    if (!location?.is_delivery_cutoff_overrides_enabled) {
      return
    }

    const addressSignature = deliveryAddressSignature(cart.delivery_address)
    if (automaticPolicyAddressSignatureRef.current === addressSignature) {
      return
    }
    automaticPolicyAddressSignatureRef.current = addressSignature

    refreshSavedAddressDeliveryPolicy({
      ...cart.delivery_address,
      country: cart.delivery_address.country ?? Country.UnitedStates.Value,
    })
  }, [cart, location?.is_delivery_cutoff_overrides_enabled, open])

  const setDeliveryAddressFields = useCallback(
    (addressFields) => {
      if (Object.hasOwn(addressFields, 'name')) {
        form.setFieldValue('deliveryAddress.name', addressFields.name ?? '')
      }

      form.setFieldValue('deliveryAddress.line1', addressFields.line1 ?? '')
      form.setFieldValue('deliveryAddress.line2', addressFields.line2 ?? '')
      form.setFieldValue('deliveryAddress.city', addressFields.city ?? '')
      form.setFieldValue('deliveryAddress.state', normalizeAddressRegionCode(addressFields.state))
      form.setFieldValue('deliveryAddress.postal_code', addressFields.postal_code ?? '')
      form.setFieldValue(
        'deliveryAddress.postal_code_suffix',
        addressFields.postal_code_suffix ?? null
      )
      form.setFieldValue('deliveryAddress.google_place_id', addressFields.google_place_id ?? null)
      form.setFieldValue('deliveryAddress.is_verified', Boolean(addressFields.is_verified))
    },
    [form]
  )

  const invalidateDeliveryAddressSelection = () => {
    form.setFieldValue('deliveryAddress.postal_code_suffix', null)
    clearDeliveryPolicy()
    form.setFieldValue('deliveryAddress.name', '')
    form.setFieldValue('deliveryAddress.google_place_id', null)
    form.setFieldValue('deliveryAddress.is_verified', false)
    acceptedTypedAddressRef.current = null
    updatePlaceMeta({
      lat: null,
      lng: null,
      placeId: null,
      isRooftop: null,
    })
    setIsOutsideAreaDelivery(false)
    clearFieldErrors(form, 'recipientPhone')
  }

  const handlePlaceSelected = async (place) => {
    clearDeliveryPolicy()
    const selectedAddressFields = buildOrderDeliveryAddressFieldsFromPlace(place)
    const selectedPlaceMeta = {
      lat: typeof place.lat === 'number' ? place.lat : null,
      lng: typeof place.lng === 'number' ? place.lng : null,
      placeId: place.placeId ?? null,
      isRooftop: null,
    }

    acceptedTypedAddressRef.current = null
    setIsOutsideAreaDelivery(false)
    clearFieldErrors(form, 'recipientPhone')
    form.setFieldValue('deliveryAddress.name', selectedAddressFields.name)

    form.setFieldValue('deliveryAddress.line1', selectedAddressFields.line1)
    form.setFieldValue('deliveryAddress.line2', selectedAddressFields.line2)
    form.setFieldValue('deliveryAddress.city', selectedAddressFields.city)
    form.setFieldValue('deliveryAddress.state', selectedAddressFields.state)
    form.setFieldValue('deliveryAddress.postal_code', selectedAddressFields.postal_code)
    form.setFieldValue(
      'deliveryAddress.postal_code_suffix',
      selectedAddressFields.postal_code_suffix
    )
    form.setFieldValue('deliveryAddress.google_place_id', selectedPlaceMeta.placeId)
    form.setFieldValue('deliveryAddress.is_verified', Boolean(selectedPlaceMeta.placeId))

    updatePlaceMeta(selectedPlaceMeta)

    if (place.placeId) {
      const geocodedPlace = await geocodePlaceId(place.placeId)
      const isRooftop = resolveIsRooftop(geocodedPlace)

      const current = placeMetaRef.current

      if (current.placeId !== place.placeId) {
        return
      }

      if (
        !selectedAddressFields.postal_code_suffix &&
        geocodedPlace?.postal_code === selectedAddressFields.postal_code &&
        geocodedPlace?.postal_code_suffix
      ) {
        selectedAddressFields.postal_code_suffix = geocodedPlace.postal_code_suffix
        form.setFieldValue('deliveryAddress.postal_code_suffix', geocodedPlace.postal_code_suffix)
      }

      updatePlaceMeta({
        ...current,
        isRooftop,
      })
    }

    const resolvedPlaceMeta = placeMetaRef.current
    await refreshDeliveryPolicy({
      ...selectedAddressFields,
      country: Country.UnitedStates.Value,
      google_place_id: resolvedPlaceMeta.placeId,
      is_verified: Boolean(resolvedPlaceMeta.placeId),
      is_rooftop: Boolean(resolvedPlaceMeta.isRooftop),
      lat: resolvedPlaceMeta.lat,
      lng: resolvedPlaceMeta.lng,
    })
  }

  const clearDeliveryAddressFields = () => {
    clearDeliveryPolicy()
    acceptedTypedAddressRef.current = null
    setIsOutsideAreaDelivery(false)
    clearFieldErrors(form, 'recipientPhone')
    form.setFieldValue('deliveryAddress.name', '')
    form.setFieldValue('deliveryAddress.line1', '')
    form.setFieldValue('deliveryAddress.line2', '')
    form.setFieldValue('deliveryAddress.city', '')
    form.setFieldValue('deliveryAddress.state', '')
    form.setFieldValue('deliveryAddress.postal_code', '')
    form.setFieldValue('deliveryAddress.postal_code_suffix', null)
    form.setFieldValue('deliveryAddress.google_place_id', null)
    form.setFieldValue('deliveryAddress.is_verified', false)
    updatePlaceMeta({ lat: null, lng: null, placeId: null, isRooftop: null })
  }

  const handleRecipientSelected = (recipient) => {
    clearDeliveryPolicy()
    form.setFieldValue('recipientName', recipient?.name || '')
    form.setFieldValue('recipientPhone', recipient?.phone || '')
    form.setFieldValue('recipientEmail', recipient?.email || '')

    const address = recipient?.delivery_address
    setIsOutsideAreaDelivery(false)
    clearFieldErrors(form, 'recipientPhone')

    if (!address) {
      clearDeliveryAddressFields()
      return
    }

    form.setFieldValue(
      'deliveryAddress.name',
      resolveDeliveryDestinationName(address, recipient?.name)
    )

    const limitedAddressFields = limitOrderDeliveryAddressFields(address)

    form.setFieldValue('deliveryAddress.line1', limitedAddressFields.line1)
    form.setFieldValue('deliveryAddress.line2', limitedAddressFields.line2)
    form.setFieldValue('deliveryAddress.city', limitedAddressFields.city)
    form.setFieldValue('deliveryAddress.state', limitedAddressFields.state)
    form.setFieldValue('deliveryAddress.postal_code', limitedAddressFields.postal_code)
    form.setFieldValue(
      'deliveryAddress.postal_code_suffix',
      limitedAddressFields.postal_code_suffix
    )
    const googlePlaceId = address.google_place_id || null
    const isVerified = Boolean(googlePlaceId && address.is_verified)

    form.setFieldValue('deliveryAddress.google_place_id', googlePlaceId)
    form.setFieldValue('deliveryAddress.is_verified', isVerified)

    updatePlaceMeta({
      lat: address.lat ?? null,
      lng: address.lng ?? null,
      placeId: googlePlaceId,
      isRooftop: googlePlaceId ? (address.is_rooftop ?? null) : null,
    })

    void refreshDeliveryPolicy({
      ...limitedAddressFields,
      country: address.country ?? Country.UnitedStates.Value,
      google_place_id: googlePlaceId,
      is_verified: isVerified,
      is_rooftop: address.is_rooftop ?? false,
      lat: address.lat ?? null,
      lng: address.lng ?? null,
    })
  }

  const handleAddNewRecipient = () => {
    form.setFieldValue('recipientName', '')
    form.setFieldValue('recipientPhone', '')
    form.setFieldValue('recipientEmail', '')
    clearDeliveryAddressFields()
  }

  const handleClose = () => {
    form.reset(getDeliveryFormDefaults(cart))
    acceptedTypedAddressRef.current = null
    pendingVerifiedAddressRef.current = null
    clearDeliveryPolicy()
    setIsOutsideAreaDelivery(canUseSavedOutsideAreaDelivery(cart))
    updatePlaceMeta({
      lat: cart?.delivery_address?.lat ?? null,
      lng: cart?.delivery_address?.lng ?? null,
      placeId: cart?.delivery_address?.google_place_id || null,
      isRooftop: cart?.delivery_address?.is_rooftop ?? null,
    })
    onOpenChange(false)
  }

  const handleDialogOpenChange = (isOpen) => {
    if (!isOpen) {
      handleClose()
    }
  }

  const suggestedIsUsable = () => {
    return isSuggestedAddressUsable(verifyPrompt?.suggested)
  }

  const resumeSubmitAfterVerificationChoice = () => {
    queueMicrotask(() => {
      void form.handleSubmit()
    })
  }

  const handleUseTypedAddress = () => {
    clearDeliveryPolicy()
    const typedAddress = verifyPrompt?.typedAddress ?? {}
    const nextDeliveryAddress = {
      ...typedAddress,
      country: Country.UnitedStates.Value,
      postal_code_suffix: null,
      google_place_id: null,
      lat: null,
      lng: null,
      is_verified: false,
      is_rooftop: false,
    }

    acceptedTypedAddressRef.current = deliveryAddressSignature(nextDeliveryAddress)
    pendingVerifiedAddressRef.current = nextDeliveryAddress
    setDeliveryAddressFields(nextDeliveryAddress)
    updatePlaceMeta({ lat: null, lng: null, placeId: null, isRooftop: false })
    setVerifyPrompt(null)
    resumeSubmitAfterVerificationChoice()
  }

  const handleUseSuggestedAddress = () => {
    clearDeliveryPolicy()
    const suggested = verifyPrompt?.suggested
    if (!suggested) {
      setVerifyPrompt(null)
      return
    }

    const typedAddress = verifyPrompt?.typedAddress ?? {}
    const limitedAddressFields = buildLimitedOrderDeliveryAddressFields(suggested, typedAddress)
    const placeId = suggested.placeId ?? null
    const nextPlaceMeta = {
      lat: suggested.lat ?? null,
      lng: suggested.lng ?? null,
      placeId,
      isRooftop: resolveIsRooftop(suggested),
    }
    const nextDeliveryAddress = {
      ...typedAddress,
      ...limitedAddressFields,
      country: Country.UnitedStates.Value,
      google_place_id: placeId,
      lat: nextPlaceMeta.lat,
      lng: nextPlaceMeta.lng,
      is_verified: Boolean(placeId),
      is_rooftop: Boolean(placeId ? nextPlaceMeta.isRooftop : false),
    }

    acceptedTypedAddressRef.current = null
    pendingVerifiedAddressRef.current = nextDeliveryAddress
    setDeliveryAddressFields(nextDeliveryAddress)
    updatePlaceMeta(nextPlaceMeta)
    setVerifyPrompt(null)
    resumeSubmitAfterVerificationChoice()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          size="large"
          viewport="contained"
          padding="none"
          spacing="none"
          className="!flex max-h-[calc(100dvh-1rem)] flex-col sm:max-h-[calc(100dvh-2rem)]"
          // Prevent autofocus on the address input if the address is already set
          onOpenAutoFocus={(e) => (cart?.delivery_address?.line1 ? e.preventDefault() : null)}
        >
          <DialogHeader className="shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>Delivery Information</DialogTitle>
            <DialogDescription>Enter delivery address and select date</DialogDescription>
          </DialogHeader>

          <form.AppForm>
            <form.Form className="min-h-0">
              <ScrollArea
                className="max-h-[calc(100dvh-16rem)] sm:max-h-[calc(100dvh-13rem)]"
                viewportClassName="max-h-[calc(100dvh-16rem)] overscroll-y-contain sm:max-h-[calc(100dvh-13rem)]"
              >
                <div className="space-y-4 px-6 pb-6">
                  <div className="space-y-4">
                    <RecipientSearch
                      cart={cart}
                      fulfillmentType="delivery"
                      onRecipientSelected={handleRecipientSelected}
                      onClearForm={handleAddNewRecipient}
                    />

                    <form.AppField name="recipientName">
                      {(field) => (
                        <field.TextInput
                          id="delivery-recipient-name"
                          label="Recipient Name"
                          placeholder="Who is this order for?"
                          maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_name}
                          characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_name}
                        />
                      )}
                    </form.AppField>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <form.AppField name="recipientPhone">
                        {(field) => (
                          <field.PhoneInput
                            id="delivery-recipient-phone"
                            label={
                              requiresOutsideAreaRecipientPhone
                                ? 'Phone (Required for outside-area delivery)'
                                : 'Phone (Optional)'
                            }
                            maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_phone}
                            characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_phone}
                          />
                        )}
                      </form.AppField>

                      <form.AppField name="recipientEmail">
                        {(field) => (
                          <field.EmailInput
                            id="delivery-recipient-email"
                            label="Email (Optional)"
                            placeholder="recipient@example.com"
                            maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.recipient_email}
                            characterMaxLength={ORDER_FIELD_MAX_LENGTHS.recipient_email}
                          />
                        )}
                      </form.AppField>
                    </div>
                  </div>

                  <PosDeliveryAddressFields
                    form={form}
                    onAddressSelected={handlePlaceSelected}
                    onAddressLine1Change={invalidateDeliveryAddressSelection}
                    onCoreAddressChange={invalidateDeliveryAddressSelection}
                  />

                  {/* Delivery Date */}
                  <PosFulfillmentDateField
                    form={form}
                    fieldName="deliveryDate"
                    label="Delivery Date"
                    onDateChange={(date) => {
                      if (!form.getFieldValue('preferredWeekday')) {
                        form.setFieldValue(
                          'preferredWeekday',
                          getPosSubscriptionScheduleDefaults({
                            ...cart,
                            fulfillment_due_date: date,
                          }).preferredWeekday
                        )
                      }
                    }}
                    minDate={
                      deliveryDateAvailability
                        ? dayjs(deliveryDateAvailability.min).toDate()
                        : dayjs().toDate()
                    }
                    maxDate={
                      deliveryDateAvailability ? dayjs(deliveryDateAvailability.max).toDate() : null
                    }
                    disabled={(date) =>
                      deliveryDateAvailability?.hardDisabledDates?.has(toYmdFromDate(date)) ?? false
                    }
                    modifiers={{
                      availabilityClosed: (date) =>
                        getPosFulfillmentDateBlockKind(
                          deliveryDateAvailability,
                          toYmdFromDate(date)
                        ) === 'closed',
                      availabilityUnavailable: (date) =>
                        getPosFulfillmentDateBlockKind(
                          deliveryDateAvailability,
                          toYmdFromDate(date)
                        ) === 'unavailable',
                    }}
                    availabilityBlockLabel={(date) =>
                      getPosFulfillmentDateBlockTooltip(
                        deliveryDateAvailability,
                        toYmdFromDate(date),
                        { isPickup: false }
                      )
                    }
                    className="w-full"
                  >
                    <form.Subscribe selector={(state) => state.values.deliveryDate}>
                      {(deliveryDate) => {
                        const dateWarning = isDeliveryDatePastCutoff(deliveryPolicy, deliveryDate)
                          ? {
                              title: 'Delivery date is past the configured cutoff',
                              description: `This address has a ${deliveryPolicy.effective_delivery_cutoff}-business-hour delivery cutoff. You can still use this date for this POS order if you can fulfill it.`,
                            }
                          : getPosFulfillmentDateWarning(deliveryDateAvailability, deliveryDate, {
                              isPickup: false,
                            })

                        return dateWarning ? (
                          <Alert variant="warning">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertTitle>{dateWarning.title}</AlertTitle>
                            <AlertDescription>{dateWarning.description}</AlertDescription>
                          </Alert>
                        ) : null
                      }}
                    </form.Subscribe>
                  </PosFulfillmentDateField>

                  <PosSubscriptionScheduleField
                    form={form}
                    cart={cart}
                    dateFieldName="deliveryDate"
                    fulfillmentType="delivery"
                  />

                  {/* Special Instructions */}
                  <PosSpecialInstructionsField form={form} />
                </div>
              </ScrollArea>

              <DialogFooter className="shrink-0 border-t border-border/70 px-6 py-4 sm:items-center">
                <Button
                  className="w-full sm:w-auto"
                  variant="outline"
                  onClick={handleClose}
                  type="button"
                >
                  Cancel
                </Button>
                <form.SubmitButton
                  className="w-full sm:w-auto"
                  disableWhenInvalid={false}
                  keepChildrenWhileLoading
                >
                  Confirm
                </form.SubmitButton>
              </DialogFooter>
            </form.Form>
          </form.AppForm>
        </DialogContent>
      </Dialog>

      <AddressVerificationDialog
        open={Boolean(verifyPrompt)}
        typedAddress={verifyPrompt?.typedAddress}
        suggested={verifyPrompt?.suggested}
        isSuggestedUsable={suggestedIsUsable()}
        onChooseTyped={handleUseTypedAddress}
        onChooseSuggested={handleUseSuggestedAddress}
        onCancel={() => setVerifyPrompt(null)}
      />
    </>
  )
}
